import type { EventDataDto, TemplateContentDto } from "../../autogen/bff-api";
import { isExemptionMarker } from "../../pages/sourcing-events/buyer/wizard/utils/publicProcurementOptions";

// Which parts of an event a template is being built from. The wizard offers both the whole event and
// a single section, and the choice travels to the editor as a query param.
export type TemplateSection = "minimum" | "selection" | "award";

export const ALL_SECTIONS: TemplateSection[] = ["minimum", "selection", "award"];

const isSection = (value: string): value is TemplateSection =>
  (ALL_SECTIONS as string[]).includes(value);

// A missing, empty or unrecognised param means "the whole event" — a hand-edited URL degrades to the
// widest sensible scope rather than to an empty template.
export const parseSections = (raw: string | null): TemplateSection[] => {
  if (!raw) return ALL_SECTIONS;
  const parsed = raw.split(",").filter(isSection);
  return parsed.length > 0 ? parsed : ALL_SECTIONS;
};

/**
 * Snapshot the requested sections of a live event as template content.
 *
 * Only the requested sections are set at all — an unselected section is absent rather than empty, so
 * a section-scoped template does not claim to define the others. Ids are stripped throughout: a
 * template is a snapshot, not a reference, and fresh ids are assigned when it is later applied.
 */
export const buildTemplateContent = (
  eventData: EventDataDto,
  sections: TemplateSection[]
): TemplateContentDto => {
  const content: TemplateContentDto = {};

  if (sections.includes("minimum")) {
    content.minimumRequirements = eventData.minimumRequirements.map((r) => ({
      title: r.title,
      description: r.description,
      responseType: r.responseType,
    }));
  }

  if (sections.includes("selection")) {
    content.selectionCriteria = eventData.selectionCriteria.map((c) => ({
      type: c.type,
      name: c.name,
      description: c.description,
      requiresEvidence: c.requiresEvidence,
    }));
  }

  if (sections.includes("award")) {
    const award = eventData.awardCriteria;
    if (award?.bestPriceQualityRatio) {
      // § 5c exemption markers are not criteria — they are how the live event records the justification
      // for omitting an environment criterion, rendered as a text box and filtered out of the weights
      // table. The template editor has no exemption concept (dropped in v1) and would show them as
      // ordinary criterion cards, so both the markers and the justification text are left behind.
      const criteria = (award.bestPriceQualityRatio.criteria ?? [])
        .filter((c) => !isExemptionMarker(c.type))
        .map((c) => ({
          type: c.type,
          name: c.name,
          description: c.description,
          weight: c.weight,
          // Sub-criteria are part of what the criterion *is*, so a template that dropped them would
          // hand back a criterion weighted 40 % with nothing saying how those 40 % are judged.
          subCriteria: (c.subCriteria ?? []).map((s) => ({
            name: s.name,
            description: s.description,
            weight: s.weight,
            answerFormat: s.answerFormat,
            requiresDocumentation: s.requiresDocumentation,
          })),
        }));
      content.awardCriteria = { lowestPrice: null, bestPriceQualityRatio: { criteria } };
    } else if (award?.lowestPrice) {
      content.awardCriteria = { lowestPrice: { selected: true }, bestPriceQualityRatio: null };
    }
    // The price model configures how price scores are computed under an award model, so it travels
    // with the award section rather than on its own.
    if (eventData.priceModel) content.priceModel = eventData.priceModel;
  }

  return content;
};
